/**
 * Session state for React.
 *
 * Four states, and the app renders all four: `loading` while `/api/me` is in
 * flight (one request, on boot), `guest` when it says no, `authenticated` when
 * it says yes, and `offline` when it could not be asked but this device
 * remembers who was last signed in. Guest is a first-class state, not an error —
 * a signed-out user gets the whole app, local-only, exactly as M2 shipped it.
 *
 * `refresh()` is exposed because two things change the session without a page
 * load: signing out, and returning from a magic link or the Google redirect.
 */

import type { ReactNode } from 'react';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { Entitlement } from '@/lib/plan';
import { DEFAULT_ENTITLEMENT } from '@/lib/plan';
import type { Profile } from '@/profile/protocol';
import type { AuthConfig, AuthUser, SessionResponse } from './client';
import { fetchAuthConfig, fetchSession, UNKNOWN_AUTH_CONFIG } from './client';

/**
 * What the app knows about who is signed in.
 *
 * `offline` is named after the sync client's own status of the same name
 * (`sync/sync-client.ts`, and the sidebar indicator in `components/sync-status.tsx`),
 * because it is the same condition seen one layer earlier: the server cannot be
 * reached, the work is local, and nothing is lost. The two arrive together in
 * practice — a boot with no network shows *Offline* in the sidebar because the
 * socket cannot open, and the session says `offline` because `/api/me` could
 * not be asked.
 *
 * **What `offline` means for the workspace, which is the reason it exists.** The
 * store picks its IndexedDB record by `user.id`, so `user` being right matters
 * more than `status` being flattering: `offline` carries a non-null `user`, and
 * the brewer's own recipes open. Before this state existed, an offline boot
 * opened the guest record and showed an empty app. See `identity-cache.ts`.
 *
 * **What it means for everything else.** Surfaces that genuinely need the server
 * keep gating on `status === 'authenticated'` — the assistant drawer and the
 * "Ask the assistant" affordance in the recipe list are the two, and they are
 * right to: an unreachable server cannot answer a prompt, and offering a send
 * button that would fail is the thing this codebase avoids everywhere else
 * (`/api/config`'s `UNKNOWN_AUTH_CONFIG` defaults are the same instinct — with
 * no reachable `/api/config` the billing and AI blocks read as off, and the
 * upgrade dialog degrades to the comparison table rather than crashing).
 * Surfaces about *identity* — the sidebar account row, the plan meter — check
 * `session.user !== null` and therefore keep working.
 */
export interface SessionState {
  readonly status: SessionStatus;
  readonly user: AuthUser | null;
  /** What this deployment offers. `providers.google` drives the sign-in page. */
  readonly config: AuthConfig;
  /**
   * What this account's plan allows (PLAN §9).
   *
   * The trial default for a guest and while `/api/me` is in flight — an unknown
   * plan must read as the free one, or a slow network would briefly promise
   * something the server will refuse. In the `offline` state it is the cached
   * copy of the last plan the server granted, which is display only: every
   * limit is enforced server-side at apply time.
   */
  readonly entitlement: Entitlement;
  /**
   * The display name and avatar this account has claimed, or `null`
   * (PLAN-GALLERY G1).
   *
   * `null` is the ordinary state — a profile is opt-in and most accounts never
   * claim one — and it is also what a guest, an offline boot and a pre-G1 Worker
   * produce. It carries no authority of any kind: it is what the shell *draws*,
   * and every route that acts on a profile reads the row again server-side.
   */
  readonly profile: Profile | null;
  /**
   * The admin id behind this session, when an operator started it, or `null`
   * (PLAN-OPS Phase 4).
   *
   * The app shell draws a banner whenever this is set, and that banner is the
   * feature: impersonation has to be unmistakable on screen, so the flag is the
   * server's own (`session.impersonated_by`, by way of `/api/me`) and there is no
   * way to dismiss what it renders. It authorizes nothing and is read by exactly
   * one component.
   */
  readonly impersonatedBy: string | null;
  /**
   * Re-read `/api/me`. Call after signing out, returning from a redirect, or
   * changing plan — it refreshes the session, the entitlement and the profile
   * together.
   */
  readonly refresh: () => Promise<void>;
}

/** The four states of {@link SessionState}. See there for what each one licenses. */
export type SessionStatus = 'loading' | 'guest' | 'authenticated' | 'offline';

const SessionContext = createContext<SessionState | null>(null);

/** Options for {@link SessionProvider}. Tests inject a fixed session. */
export interface SessionProviderProps {
  readonly children: ReactNode;
  /** Skip the network and use this state. */
  readonly value?: SessionState;
}

/**
 * The status a `/api/me` answer implies.
 *
 * The only interesting line is the last: a user that came from the device rather
 * than from the server is `offline`. A *guest* is a guest either way — a browser
 * that has never signed in has nothing cached, so "signed out, and I could not
 * ask" and "signed out, and the server agrees" are the same app.
 */
function statusFor(session: SessionResponse): SessionStatus {
  if (session.user === null) return 'guest';
  return session.origin === 'server' ? 'authenticated' : 'offline';
}

/** How often an authenticated tab re-validates its session. See the heartbeat effect. */
const SESSION_HEARTBEAT_MS = 6 * 60 * 60 * 1000;

export function SessionProvider({ children, value }: SessionProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [config, setConfig] = useState<AuthConfig>(UNKNOWN_AUTH_CONFIG);
  const [entitlement, setEntitlement] = useState<Entitlement>(DEFAULT_ENTITLEMENT);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const next = await fetchSession();
    setUser(next.user);
    setEntitlement(next.entitlement);
    setProfile(next.profile);
    setImpersonatedBy(next.impersonatedBy);
    setStatus(statusFor(next));
  }, []);

  useEffect(() => {
    if (value !== undefined) return;
    let cancelled = false;
    void (async () => {
      const [session, deploymentConfig] = await Promise.all([fetchSession(), fetchAuthConfig()]);
      if (cancelled) return;
      setUser(session.user);
      setEntitlement(session.entitlement);
      setProfile(session.profile);
      setImpersonatedBy(session.impersonatedBy);
      setStatus(statusFor(session));
      setConfig(deploymentConfig);
    })();
    return () => {
      cancelled = true;
    };
  }, [value]);

  /**
   * Keep a long-lived tab's session alive (and honest).
   *
   * better-auth's sliding renewal only runs when something *asks* it to
   * validate the session — and this app is WebSocket-first, so a tab left open
   * for days makes almost no session-validating HTTP requests and can ride an
   * actively-used session straight past the 7-day expiry (a production
   * incident: sync 401ing for hours, logout only when an unrelated `/api/me`
   * finally fired). Each heartbeat is one `/api/me`; when the session has aged
   * past `updateAge` the Worker forwards better-auth's re-issued cookie, so an
   * open tab never meets the cliff. And if the session *has* died anyway, the
   * heartbeat's 401 drops the app to guest within hours rather than whenever
   * fate next triggers a fetch. Six hours: well inside the one-day renewal
   * granularity, far too sparse to matter as load. The visibility listener
   * covers laptops that sleep through their timers — a machine that wakes
   * stale refreshes immediately instead of waiting out the remainder.
   */
  useEffect(() => {
    if (value !== undefined || status !== 'authenticated') return;
    let lastBeat = Date.now();
    const beat = () => {
      lastBeat = Date.now();
      void refresh();
    };
    const timer = setInterval(beat, SESSION_HEARTBEAT_MS);
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastBeat >= SESSION_HEARTBEAT_MS) beat();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [value, status, refresh]);

  /**
   * Reconcile with the server as soon as it can be reached again.
   *
   * An `offline` session is a *guess*, and it has to stop being one the moment
   * asking is possible — including the case that matters most, where the server
   * now says the session is over: `refresh` takes the ordinary 401 path,
   * `fetchSession` clears the cache, and the app drops to guest exactly as it
   * would have on a signed-out boot. A reconnected socket does not cover this on
   * its own; the sync client only calls back on an explicit `unauthorized`
   * frame, and a session that is still valid would otherwise leave the app
   * saying `offline` forever with a perfectly good connection.
   *
   * Two triggers, because they catch different failures. `online` is the network
   * coming back, which is the common case and fires immediately. Becoming
   * visible again covers everything `online` does not: a 5xx that has since been
   * fixed, a captive portal the brewer has just signed into (both of which leave
   * `navigator.onLine` true throughout), and a laptop resuming from sleep, where
   * the event order is unreliable. Both are cheap — the effect only exists while
   * the status is `offline`, and it is one request that ends the state.
   *
   * `refresh` also runs `/api/me` from the *other* directions this provider is
   * refreshed from (sign-out, a magic-link return, a plan change), so this is
   * the only trigger the offline path needs to add.
   */
  useEffect(() => {
    if (value !== undefined || status !== 'offline') return;
    const revalidate = () => {
      void refresh();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') revalidate();
    };
    window.addEventListener('online', revalidate);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', revalidate);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [value, status, refresh]);

  const derived = useMemo<SessionState>(
    () => ({ status, user, config, entitlement, profile, impersonatedBy, refresh }),
    [status, user, config, entitlement, profile, impersonatedBy, refresh],
  );

  return <SessionContext value={value ?? derived}>{children}</SessionContext>;
}

/**
 * The session, or a permanent guest when there is no provider.
 *
 * Returning a default rather than throwing keeps every M2 test that renders a
 * component in isolation working, and matches the product rule: no provider,
 * no session, still a usable app.
 */
export function useSession(): SessionState {
  return use(SessionContext) ?? GUEST_SESSION;
}

const GUEST_SESSION: SessionState = {
  status: 'guest',
  user: null,
  config: UNKNOWN_AUTH_CONFIG,
  entitlement: DEFAULT_ENTITLEMENT,
  profile: null,
  impersonatedBy: null,
  refresh: async () => undefined,
};
